import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '../../components/Toast'
import { useAuth } from '../../store/AuthContext'
import employeesService from '../../services/employeesService'
import { companiesService } from '../../services/companiesService'
import { Employee, CreateEmployeeRequest, UserRole } from '../../services/types'
import { ConfirmModal } from '../../components/ConfirmModal'

interface EmployeeFormData {
  full_name: string
  email: string
  username: string
  role: UserRole
  primary_company: number | ''
  companies: number[]
  is_ownership_eligible: boolean
}

const emptyForm: EmployeeFormData = {
  full_name: '',
  email: '',
  username: '',
  role: 'viewer',
  primary_company: '',
  companies: [],
  is_ownership_eligible: false,
}

export const EmployeesPage = () => {
  const toast = useToast()
  const navigate = useNavigate()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const isAdmin = user?.role === 'admin'

  // State
  const [search, setSearch] = useState('')
  const [filterCompany, setFilterCompany] = useState<number | ''>('')
  const [filterActive, setFilterActive] = useState<'' | 'true' | 'false'>('')
  const [filterEligible, setFilterEligible] = useState<'' | 'true' | 'false'>('')
  const [showForm, setShowForm] = useState(false)
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null)
  const [formData, setFormData] = useState<EmployeeFormData>(emptyForm)
  const [formError, setFormError] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<Employee | null>(null)

  // Fetch employees
  const { data: employees = [], isLoading } = useQuery({
    queryKey: ['employees', search, filterCompany, filterActive, filterEligible],
    queryFn: () =>
      employeesService.getAll({
        search: search || undefined,
        primary_company: filterCompany || undefined,
        is_active: filterActive === '' ? undefined : filterActive === 'true',
        is_ownership_eligible:
          filterEligible === '' ? undefined : filterEligible === 'true',
      }),
  })

  // Fetch companies for selects
  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => companiesService.getAll({ is_active: true }),
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateEmployeeRequest) => employeesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      closeForm()
      toast.success('Funcionário criado com sucesso!')
    },
    onError: (error: any) => {
      const data = error.response?.data
      const message =
        data?.detail ||
        data?.email?.[0] ||
        data?.username?.[0] ||
        data?.non_field_errors?.[0] ||
        (typeof data === 'object' ? Object.values(data).flat().join(' ') : null) ||
        'Erro ao criar funcionário.'
      setFormError(message)
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateEmployeeRequest> }) =>
      employeesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      closeForm()
      toast.success('Funcionário atualizado com sucesso!')
    },
    onError: (error: any) => {
      const data = error.response?.data
      const message =
        data?.detail ||
        data?.email?.[0] ||
        data?.username?.[0] ||
        (typeof data === 'object' ? Object.values(data).flat().join(' ') : null) ||
        'Erro ao atualizar funcionário.'
      setFormError(message)
    },
  })

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      employeesService.update(id, { is_active } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Erro ao alterar status do funcionário.')
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => employeesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      setDeleteTarget(null)
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.detail ||
          'Erro ao excluir funcionário. Verifique se não há avaliações vinculadas.'
      )
      setDeleteTarget(null)
    },
  })

  // Form handlers
  const openCreate = () => {
    setEditingEmployee(null)
    setFormData(emptyForm)
    setFormError('')
    setShowForm(true)
  }

  const openEdit = (employee: Employee) => {
    setEditingEmployee(employee)
    setFormData({
      full_name: employee.full_name,
      email: employee.email,
      username: employee.user_detail.username,
      role: employee.user_detail.role || 'viewer',
      primary_company: employee.primary_company,
      companies: employee.companies,
      is_ownership_eligible: employee.is_ownership_eligible,
    })
    setFormError('')
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingEmployee(null)
    setFormData(emptyForm)
    setFormError('')
  }

  // Auto-generate username from name
  const generateUsername = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .replace(/\s+/g, '.')
      .replace(/[^a-z0-9.]/g, '')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    if (!formData.full_name.trim()) {
      setFormError('Nome completo é obrigatório.')
      return
    }
    if (!formData.email.trim()) {
      setFormError('Email é obrigatório.')
      return
    }
    if (!formData.username.trim()) {
      setFormError('Nome de usuário é obrigatório.')
      return
    }
    if (!formData.primary_company) {
      setFormError('Empresa principal é obrigatória.')
      return
    }

    const primaryCompanyId = Number(formData.primary_company)

    const payload: CreateEmployeeRequest = {
      full_name: formData.full_name.trim(),
      email: formData.email.trim(),
      username: formData.username.trim(),
      role: formData.role,
      primary_company: primaryCompanyId,
      companies: [primaryCompanyId],
      is_ownership_eligible: formData.is_ownership_eligible,
    }

    if (editingEmployee) {
      updateMutation.mutate({ id: editingEmployee.id, data: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Funcionários
        </h1>
        {isAdmin && (
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors"
          >
            + Novo Funcionário
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Buscar
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nome, email ou usuário..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Company Filter */}
          <div className="md:w-48">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Empresa
            </label>
            <select
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value ? Number(e.target.value) : '')}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Todas</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="md:w-40">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value as '' | 'true' | 'false')}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Todos</option>
              <option value="true">Ativos</option>
              <option value="false">Inativos</option>
            </select>
          </div>

          {/* Eligibility Filter */}
          <div className="md:w-40">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Elegibilidade
            </label>
            <select
              value={filterEligible}
              onChange={(e) => setFilterEligible(e.target.value as '' | 'true' | 'false')}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Todos</option>
              <option value="true">Elegíveis</option>
              <option value="false">Não Elegíveis</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {employees.length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Ativos</p>
          <p className="text-2xl font-bold text-success">
            {employees.filter((e) => e.is_active).length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Elegíveis</p>
          <p className="text-2xl font-bold text-primary">
            {employees.filter((e) => e.is_ownership_eligible).length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Inativos</p>
          <p className="text-2xl font-bold text-danger">
            {employees.filter((e) => !e.is_active).length}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Carregando funcionários...</p>
          </div>
        ) : employees.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-4">👥</div>
            <p className="text-gray-600 dark:text-gray-400">
              Nenhum funcionário encontrado.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Funcionário
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Pontos
                  </th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Elegível
                  </th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ativo
                  </th>
                  {isAdmin && (
                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ações
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {employees.map((employee) => (
                  <tr
                    key={employee.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      !employee.is_active ? 'opacity-50' : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {employee.full_name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {employee.email} &middot; @{employee.user_detail.username}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-700 dark:text-gray-300">
                        {employee.primary_company_detail.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`font-semibold ${
                          employee.total_points > 0
                            ? 'text-success'
                            : employee.total_points < 0
                            ? 'text-danger'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {employee.total_points > 0 ? '+' : ''}
                        {employee.total_points}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          employee.is_ownership_eligible
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {employee.is_ownership_eligible ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => {
                          if (isAdmin) {
                            toggleActiveMutation.mutate({
                              id: employee.id,
                              is_active: !employee.is_active,
                            })
                          }
                        }}
                        disabled={!isAdmin || toggleActiveMutation.isPending}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                          !isAdmin ? 'cursor-default' : 'cursor-pointer'
                        } ${
                          employee.is_active
                            ? 'bg-green-500'
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                        title={employee.is_active ? 'Ativo - clique para desativar' : 'Inativo - clique para reativar'}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            employee.is_active ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    {isAdmin && (
                      <td className="px-6 py-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => navigate(`/funcionarios/${employee.id}`)}
                            className="px-3 py-1 text-sm border border-primary rounded-btn text-primary hover:bg-primary hover:text-white transition-colors"
                          >
                            Ver
                          </button>
                          <button
                            onClick={() => openEdit(employee)}
                            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => setDeleteTarget(employee)}
                            className="px-3 py-1 text-sm border border-danger rounded-btn text-danger hover:bg-danger hover:text-white transition-colors"
                          >
                            Excluir
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={closeForm} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-card shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                {editingEmployee ? 'Editar Funcionário' : 'Novo Funcionário'}
              </h3>

              {!editingEmployee && (
                <div className="mb-4 p-3 bg-primary/10 border border-primary/20 rounded-btn text-sm text-gray-700 dark:text-gray-300">
                  Uma conta de acesso será criada automaticamente e o funcionário receberá um email para definir sua senha.
                </div>
              )}

              {formError && (
                <div className="mb-4 p-3 bg-danger/10 border border-danger rounded-btn text-danger text-sm">
                  {formError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => {
                      const name = e.target.value
                      setFormData({
                        ...formData,
                        full_name: name,
                        // Auto-generate username only on create and if user hasn't manually edited
                        ...(!editingEmployee && formData.username === generateUsername(formData.full_name)
                          ? { username: generateUsername(name) }
                          : {}),
                      })
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Nome completo do funcionário"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="email@exemplo.com"
                    required
                  />
                </div>

                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nome de Usuário (login) *
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/\s/g, '.') })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent font-mono"
                    placeholder="nome.sobrenome"
                    required
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Gerado automaticamente a partir do nome. Pode ser editado.
                  </p>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Perfil de Acesso *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="viewer">Colaborador - Visualiza rankings e histórico</option>
                    <option value="manager">Gestor - Pode avaliar funcionários</option>
                    <option value="admin">Administrador - Acesso total ao sistema</option>
                  </select>
                </div>

                {/* Primary Company */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Empresa Principal *
                  </label>
                  <select
                    value={formData.primary_company}
                    onChange={(e) => setFormData({ ...formData, primary_company: e.target.value ? Number(e.target.value) : '' })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  >
                    <option value="">Selecione a empresa principal</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                {/* Ownership Eligible */}
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_ownership_eligible}
                      onChange={(e) =>
                        setFormData({ ...formData, is_ownership_eligible: e.target.checked })
                      }
                      className="rounded border-gray-300 text-primary focus:ring-primary w-5 h-5"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Elegível para Ownership
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        Mínimo 1 ano de empresa + aprovação
                      </p>
                    </div>
                  </label>
                </div>

                {/* Actions */}
                <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={closeForm}
                    disabled={isSaving}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-6 py-2 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving
                      ? 'Salvando...'
                      : editingEmployee
                      ? 'Salvar Alterações'
                      : 'Criar Funcionário'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Excluir Funcionário"
        description={
          <p>
            Tem certeza que deseja excluir <strong>{deleteTarget?.full_name}</strong>?
            A conta de acesso ao sistema também será removida. Esta ação não pode ser desfeita.
          </p>
        }
        confirmText="Sim, Excluir"
        cancelText="Cancelar"
        isDanger
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}

export default EmployeesPage
